import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ServiceAreasManagementModal from '../ServiceAreasManagementModal';

interface ServiceArea {
  id: string;
  name: string;
  business_id: string;
  postal_codes: string[];
}

interface ServiceAreasModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  day: string;
  onSave: (selectedAreas: string[]) => void;
  currentAreas?: string[];
}

export default function ServiceAreasModal({
  isOpen,
  onClose,
  businessId,
  day,
  onSave,
  currentAreas = []
}: ServiceAreasModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>(currentAreas);
  const [loading, setLoading] = useState(true);
  const [isServiceAreasManagementModalOpen, setIsServiceAreasManagementModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      fetchServiceAreas();
      // Update selected areas when currentAreas changes
      setSelectedAreas(currentAreas);
    }
  }, [isOpen, businessId, currentAreas]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const fetchServiceAreas = async () => {
    if (!supabase) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      console.log('Fetched service areas:', data);
      setServiceAreas(data || []);
    } catch (err) {
      console.error('Error fetching service areas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleArea = (areaId: string) => {
    console.log('Toggling area:', areaId);
    setSelectedAreas(prev => 
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleSave = () => {
    console.log('Saving selected areas:', selectedAreas);
    onSave(selectedAreas);
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10060]`}
      onClick={onClose}
      style={{
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="h-full flex flex-col">
          {/* Header - Matching CreateBookingModal style */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  Select Service Areas for {day}
                </h2>
              </div>
              <button
                onClick={() => setIsServiceAreasManagementModalOpen(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                New Area
              </button>
            </div>
          </div>

          {/* Content */}
          <div 
            className="flex-1 overflow-y-auto bg-gray-50"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="p-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading service areas...</p>
                  </div>
                </div>
              ) : serviceAreas.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm font-medium text-gray-900">No service areas found</p>
                  <p className="mt-2 text-sm text-gray-500 mb-4">
                    Create service areas to get started.
                  </p>
                  <button
                    onClick={() => setIsServiceAreasManagementModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Service Area
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <button
                      onClick={() => setIsServiceAreasManagementModalOpen(true)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Area
                    </button>
                  </div>
                <div className="space-y-3">
                  {serviceAreas.map((area) => (
                    <label
                      key={area.id}
                      className="flex items-start p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAreas.includes(area.id)}
                        onChange={() => handleToggleArea(area.id)}
                        className="h-4 w-4 mt-1 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">{area.name}</div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {area.postal_codes.map((code, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700"
                            >
                              {code}
                            </span>
                          ))}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-white">
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas Management Modal */}
      <ServiceAreasManagementModal
        isOpen={isServiceAreasManagementModalOpen}
        onClose={() => {
          setIsServiceAreasManagementModalOpen(false);
          // Refresh service areas when the management modal closes
          fetchServiceAreas();
        }}
      />
    </div>
  );
} 